import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { supabase } from '@/lib/supabase'
import type { User, Session } from '@supabase/supabase-js'

interface AuthState {
  user: User | null
  session: Session | null
  loading: boolean
  error: string | null

  // Actions
  initialize: () => Promise<void>
  signIn: (email: string, password: string) => Promise<void>
  signUp: (email: string, password: string) => Promise<void>
  signOut: () => Promise<void>
  clearError: () => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      session: null,
      loading: true,
      error: null,

      initialize: async () => {
        try {
          // Get initial session
          const { data: { session }, error } = await supabase.auth.getSession()

          if (error) {
            console.error('Auth initialization error:', error)
            set({ loading: false, error: error.message })
            return
          }

          set({
            session,
            user: session?.user ?? null,
            loading: false,
          })

          // Listen for auth changes
          supabase.auth.onAuthStateChange((_event, session) => {
            set({
              session,
              user: session?.user ?? null,
            })
          })
        } catch (error) {
          console.error('Auth initialization error:', error)
          set({
            loading: false,
            error: error instanceof Error ? error.message : 'Failed to initialize auth',
          })
        }
      },

      signIn: async (email: string, password: string) => {
        set({ loading: true, error: null })

        try {
          const { data, error } = await supabase.auth.signInWithPassword({
            email,
            password,
          })

          if (error) {
            set({ loading: false, error: error.message })
            return
          }

          set({
            session: data.session,
            user: data.user,
            loading: false,
          })
        } catch (error) {
          set({
            loading: false,
            error: error instanceof Error ? error.message : 'Sign in failed',
          })
        }
      },

      signUp: async (email: string, password: string) => {
        set({ loading: true, error: null })

        try {
          const { data, error } = await supabase.auth.signUp({
            email,
            password,
          })

          if (error) {
            set({ loading: false, error: error.message })
            return
          }

          // Note: User may need to verify email depending on Supabase settings
          set({
            session: data.session,
            user: data.user,
            loading: false,
          })
        } catch (error) {
          set({
            loading: false,
            error: error instanceof Error ? error.message : 'Sign up failed',
          })
        }
      },

      signOut: async () => {
        set({ loading: true, error: null })

        try {
          const { error } = await supabase.auth.signOut()

          if (error) {
            set({ loading: false, error: error.message })
            return
          }

          set({
            session: null,
            user: null,
            loading: false,
          })
        } catch (error) {
          set({
            loading: false,
            error: error instanceof Error ? error.message : 'Sign out failed',
          })
        }
      },

      clearError: () => set({ error: null }),
    }),
    {
      name: 'xframe-auth',
      partialize: () => ({
        // Don't persist sensitive data - Supabase handles session persistence
      }),
    }
  )
)
